// Pure keyboard-entry parsing for DateBox, ported from the design. Handles single dates, ranges
// with localized "from/until" keywords, open ∞ ends, and embedded times.

import { formatIsoDate } from './dateCore';
import type { DateLocaleData } from './dateLocale';

export interface SplitDateTime {
  date: string | null;
  h: number | null;
  m: number | null;
}

/** Split a stored endpoint (`"2025-07-05T09:40"`, `"INF"`, `"-INF"`, or a bare date) into parts. */
export function splitDateTime(value: string | null): SplitDateTime {
  if (!value) return { date: null, h: null, m: null };
  if (value === 'INF' || value === '-INF') return { date: value, h: null, m: null };
  const parts = String(value).split('T');
  const date = parts[0] || null;
  let h: number | null = null;
  let m: number | null = null;
  if (parts[1]) {
    const match = /(\d{1,2})\D+(\d{1,2})/.exec(parts[1]);
    if (match) {
      h = Math.min(23, Number(match[1]));
      m = Math.min(59, Number(match[2]));
    }
  }
  return { date, h, m };
}

/** Pull `HH:mm` times out of a typed string, returning the leftover text and the times found. */
export function extractTimes(input: string): {
  clean: string;
  times: Array<{ h: number; m: number }>;
} {
  const times: Array<{ h: number; m: number }> = [];
  const clean = String(input || '').replace(
    /(\d{1,2}):(\d{1,2})/g,
    (_full, a: string, b: string) => {
      const h = Number(a);
      const m = Number(b);
      if (h <= 23 && m <= 59) times.push({ h, m });
      return ' ';
    }
  );
  return { clean, times };
}

/** Meze pro pivot dvojciferného roku (ISO datum, stejné jako `min`/`max` na DateBoxu). */
export interface DateBounds {
  min?: string | null;
  max?: string | null;
}

function yearOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const year = Number(iso.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

/**
 * Dvojciferný rok → čtyřciferný. Když jsou nastavené meze (min/max) a vyhovuje jim právě jedno
 * století, zvolí ho (např. max = letos → 00–26 = 20xx, 27–99 = 19xx). Jinak výchozí pivot
 * POSIX strptime %y (00–68 → 20xx, 69–99 → 19xx).
 */
function expandTwoDigitYear(yy: number, bounds?: DateBounds): number {
  const minYear = yearOf(bounds?.min);
  const maxYear = yearOf(bounds?.max);
  const inBounds = [2000 + yy, 1900 + yy].filter(
    (y) => (minYear == null || y >= minYear) && (maxYear == null || y <= maxYear)
  );
  if (inBounds.length === 1) return inBounds[0]!;
  return yy <= 68 ? 2000 + yy : 1900 + yy;
}

/** Parse one typed date respecting locale order (en = M/D/Y, otherwise D/M/Y) → ISO or null. */
export function parseOneDate(
  input: string,
  locale: DateLocaleData,
  bounds?: DateBounds
): string | null {
  const tokens = input.match(/\d+/g);
  if (!tokens || tokens.length < 3) return null;
  let d: number;
  let m: number;
  let y: number;
  if (locale.code === 'en') {
    m = Number(tokens[0]);
    d = Number(tokens[1]);
    y = Number(tokens[2]);
  } else {
    d = Number(tokens[0]);
    m = Number(tokens[1]);
    y = Number(tokens[2]);
  }
  if (y < 100) y = expandTwoDigitYear(y, bounds);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (date.getMonth() !== m - 1) return null;
  return formatIsoDate(date);
}

export interface ParsedRange {
  from: string | null;
  to: string | null;
}

/**
 * Parse a typed range. Two dates → closed range (auto-swapped). One date with a leading dash or a
 * "from"/"until" keyword → open ∞ end (subject to `openStart`/`openEnd`). Returns `null` on invalid.
 */
export function parseRange(
  text: string,
  locale: DateLocaleData,
  opts: { openStart: boolean; openEnd: boolean; bounds?: DateBounds }
): ParsedRange | null {
  const clean = (text || '').trim();
  if (clean === '') return { from: null, to: null };
  const low = clean.toLowerCase();
  const dates = clean.match(/\d{1,2}\s*[./-]\s*\d{1,2}\s*[./-]\s*\d{2,4}/g) ?? [];
  const kwTo = ['do', 'to', 'until', locale.rangeTo.toLowerCase()];
  const kwFrom = ['od', 'from', locale.rangeFrom.toLowerCase()];
  const startsTo = /^[-–—]/.test(clean) || kwTo.some((k) => low.startsWith(`${k} `));
  const explicitFrom = kwFrom.some((k) => low.startsWith(`${k} `));

  if (dates.length >= 2) {
    const a = parseOneDate(dates[0]!, locale, opts.bounds);
    const b = parseOneDate(dates[1]!, locale, opts.bounds);
    if (!a || !b) return null;
    return b < a ? { from: b, to: a } : { from: a, to: b };
  }
  if (dates.length === 1) {
    const d = parseOneDate(dates[0]!, locale, opts.bounds);
    if (!d) return null;
    if (startsTo) return opts.openStart ? { from: '-INF', to: d } : null;
    if (explicitFrom) return opts.openEnd ? { from: d, to: 'INF' } : null;
    // Holé datum: u otevřeného intervalu znamená [datum, ∞); u `finite` (openEnd=false) je to
    // nekompletní rozsah čekající na druhý konec.
    return opts.openEnd ? { from: d, to: 'INF' } : { from: d, to: null };
  }
  return null;
}
